import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MovieList from '../components/MovieList';
import { useMovies } from '../hooks/useMovies';
import { useOrganizers } from '../hooks/useOrganizers';
import { getSubdomain, normalizeLabel } from '../lib/tenant';
import { Session } from '@supabase/supabase-js';
import { Movie } from '../types';

interface OrganizersPageProps {
    session: Session | null;
    onBook: (movie: Movie) => void;
}

export default function OrganizersPage({ session, onBook }: OrganizersPageProps) {
    const navigate = useNavigate();
    const { data: movies = [] } = useMovies();
    const { data: organizers = [] } = useOrganizers();

    // Local state for organizer selection/rotation
    const [selectedOrganizerIndex, setSelectedOrganizerIndex] = useState(0);
    const [orgOverlayIndex, setOrgOverlayIndex] = useState<number | null>(null);

    // Gallery rotation
    const [orgGalleryIndex, setOrgGalleryIndex] = useState(0);
    const [orgGalleryOverlayIndex, setOrgGalleryOverlayIndex] = useState<number | null>(null);

    const subdomain = getSubdomain(window.location.hostname);
    const isTenant = !!subdomain;

    const tenantMovies = isTenant
        ? movies.filter(m => normalizeLabel(m.organizer || '') === normalizeLabel(subdomain || ''))
        : movies;

    const tenantOrganizers = isTenant
        ? organizers.filter(o => normalizeLabel(o.name) === normalizeLabel(subdomain || ''))
        : organizers;

    const activeOrg = tenantOrganizers.length > 0
        ? tenantOrganizers[Math.min(selectedOrganizerIndex, Math.max(tenantOrganizers.length - 1, 0))]
        : null;
    const activeGallery = activeOrg?.gallery_images || [];
    const hasGallery = activeGallery.length > 0 && activeOrg?.is_gallery_enabled !== false;

    // Gallery rotation effect
    useEffect(() => {
        if (hasGallery) {
            const interval = setInterval(() => {
                const next = (orgGalleryIndex + 1) % activeGallery.length;
                setOrgGalleryOverlayIndex(next);
                setTimeout(() => {
                    setOrgGalleryIndex(next);
                    setOrgGalleryOverlayIndex(null);
                }, 800);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [hasGallery, activeGallery.length, orgGalleryIndex]);

    // Reset gallery when organizer changes
    useEffect(() => {
        setOrgGalleryIndex(0);
        setOrgGalleryOverlayIndex(null);
    }, [selectedOrganizerIndex]);

    const orgMovies = activeOrg
        ? tenantMovies.filter(m => m.organizer_id === activeOrg.id)
        : [];

    return (
        <>
            <div className="relative h-[600px] overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={
                            hasGallery
                                ? activeGallery[orgGalleryIndex]
                                : tenantOrganizers.length > 0
                                    ? tenantOrganizers[Math.min(selectedOrganizerIndex, Math.max(tenantOrganizers.length - 1, 0))].image_url
                                    : 'https://i.postimg.cc/02Y8y6Yt/Whats-App-Image-2025-03-24-at-13-08-06-1.jpg'
                        }
                        alt={tenantOrganizers.length > 0 ? tenantOrganizers[Math.min(selectedOrganizerIndex, Math.max(tenantOrganizers.length - 1, 0))].name : 'Organizer'}
                        className="w-full h-full object-cover"
                    />

                    {orgGalleryOverlayIndex !== null && hasGallery && (
                        <img
                            src={activeGallery[orgGalleryOverlayIndex]}
                            alt="Gallery Overlay"
                            className="w-full h-full object-cover absolute inset-0 animate-fade-in"
                        />
                    )}

                    {orgOverlayIndex !== null && tenantOrganizers.length > 0 && !hasGallery && (
                        <img
                            src={tenantOrganizers[orgOverlayIndex].image_url}
                            alt={tenantOrganizers[orgOverlayIndex].name}
                            className="w-full h-full object-cover absolute inset-0 animate-fade-in"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/50 to-white dark:from-gray-900/70 dark:via-gray-900/50 dark:to-gray-900"></div>
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
                    <div className="max-w-2xl animate-fade-in">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                            Movies by organizer
                        </h1>
                        {tenantOrganizers.length > 0 && (
                            <p className="text-xl text-gray-700 dark:text-gray-200 mb-6">
                                {tenantOrganizers[Math.min(selectedOrganizerIndex, Math.max(tenantOrganizers.length - 1, 0))].name}
                            </p>
                        )}
                        {tenantOrganizers.length > 1 && (
                            <div className="flex flex-wrap gap-2 mb-8">
                                {tenantOrganizers.map((org, idx) => (
                                    <button
                                        key={org.id}
                                        onClick={() => {
                                            setOrgOverlayIndex(idx);
                                            setTimeout(() => {
                                                setSelectedOrganizerIndex(idx);
                                                setOrgOverlayIndex(null);
                                            }, 800);
                                        }}
                                        className={
                                            idx === selectedOrganizerIndex
                                                ? "px-3 py-1 rounded-full bg-indigo-600 text-white border border-indigo-500"
                                                : "px-3 py-1 rounded-full bg-gray-700 text-gray-200 border border-gray-600 hover:bg-gray-600"
                                        }
                                    >
                                        {org.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        <button
                            onClick={() => {
                                const moviesSection = document.getElementById('movies-section');
                                moviesSection?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="px-8 py-4 rounded-lg text-lg font-semibold bg-gray-800 text-white dark:bg-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 animate-glow"
                        >
                            Browse Movies
                        </button>
                    </div>
                </div>
            </div>
            <main id="movies-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 scroll-mt-24">
                {tenantOrganizers.length > 0 ? (
                    orgMovies.length > 0 ? (
                        <MovieList
                            movies={orgMovies}
                            session={session}
                            onViewDetails={(id) => navigate(`/movie/${id}`)}
                            onBookMovie={onBook}
                        />
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-xl text-gray-300">No current events</p>
                        </div>
                    )
                ) : (
                    <div className="text-center py-12">
                        <p className="text-xl text-gray-300">No organizers</p>
                    </div>
                )}
            </main>
        </>
    );
}
